import React from 'react';
import Modal from 'shared/components/misc/modal.jsx';
import UIRegions from 'shared/constants/UIRegions';
import { isMac } from 'shared/lib/platformCheck';
import { Row, Col, Input } from 'bootstrap';
import { Icon } from '@shoflo-rundown/polymer.legacy';
import classnames from 'classnames';

const RowSection = 'row';
const ColumnSection = 'column';
const CellSection = 'cell';
const TrackingSection = 'tracking';
const MiscSection = 'misc';
const GlobalElementSection = 'globalElement';

/**
 * Label: the shortcut label
 * Items: array containing the macOS version as first item and winOS version as second item (see hotKeyMixin.js for reference)
 */
const KeyboardShortcuts = [
  { section: RowSection, label: 'Select row(s)', items: ['`', '`'] },
  {
    section: RowSection,
    label: 'Delete row(s)',
    items: ['⇧/+/⌘/+/⌫', 'Ctrl/+/Shift/+/Backspace'],
  },
  { section: RowSection, label: 'Lock row(s)', items: ['⌘/+/L', 'Ctrl/+/L'] },
  {
    section: RowSection,
    label: 'Duplicate row(s)',
    items: ['⌘/+/D', 'Ctrl/+/D'],
  },
  {
    section: RowSection,
    label: 'Convert row(s) to title row',
    items: ['⌘/+/;', 'Ctrl/+/;'],
  },
  {
    section: RowSection,
    label: 'Move row(s) up',
    items: ['⌘/+/↑', 'Ctrl/+/↑'],
  },
  {
    section: RowSection,
    label: 'Move row(s) down',
    items: ['⌘/+/↓', 'Ctrl/+/↓'],
  },
  {
    section: RowSection,
    label: 'Add row above',
    items: ['⇧/+/⌘/+/↑', 'Shift/+/Ctrl/+/↑'],
  },
  {
    section: RowSection,
    label: 'Add row below',
    items: ['⇧/+/⌘/+/↓', 'Shift/+/Ctrl/+/↓'],
  },
  {
    section: RowSection,
    label: 'Add title row above',
    items: ['⌘/+/⌥/+/↑', 'Ctrl/+/Alt/+/↑'],
  },
  {
    section: RowSection,
    label: 'Add title row below',
    items: ['⌘/+/⌥/+/↓', 'Ctrl/+/Alt/+/↓'],
  },
  {
    section: RowSection,
    label: 'Copy row(s)',
    items: ['⇧/+/⌘/+/C', 'Shift/+/Ctrl/+/C'],
  },
  {
    section: RowSection,
    label: 'Cut row(s)',
    items: ['⇧/+/⌘/+/X', 'Shift/+/Ctrl/+/X'],
  },
  {
    section: RowSection,
    label: 'Paste row(s) below',
    items: ['⇧/+/⌘/+/V', 'Shift/+/Ctrl/+/V'],
  },
  {
    section: RowSection,
    label: 'Toggle row(s) global highlights',
    items: ['⇧/+/⌘/+/G', 'Shift/+/Ctrl/+/G'],
  },
  {
    section: RowSection,
    label: 'Toggle row(s) personal highlights',
    items: ['⇧/+/⌘/+/H', 'Shift/+/Ctrl/+/H'],
  },
  {
    section: RowSection,
    label: 'Select all rows',
    items: ['⌘/+/A', 'Ctrl/+/A'],
  },

  {
    section: ColumnSection,
    label: 'Grow column',
    items: ['⇧/+/⌘/+/→', 'Shift/+/Ctrl/+/→'],
  },
  {
    section: ColumnSection,
    label: 'Shrink column',
    items: ['⇧/+/⌘/+/←', 'Shift/+/Ctrl/+/←'],
  },
  {
    section: ColumnSection,
    label: 'Move column left',
    items: ['⌘/+/←', 'Ctrl/+/←'],
  },
  {
    section: ColumnSection,
    label: 'Move column right',
    items: ['⌘/+/→', 'Ctrl/+/→'],
  },
  {
    section: ColumnSection,
    label: 'Hide column',
    items: ['⌘/+/E', 'Ctrl/+/E'],
  },
  {
    section: ColumnSection,
    label: 'Show all columns',
    items: ['⇧/+/⌘/+/E', 'Shift/+/Ctrl/+/E'],
  },

  { section: CellSection, label: 'Copy cell(s)', items: ['⌘/+/C', 'Ctrl/+/C'] },
  { section: CellSection, label: 'Cut cell(s)', items: ['⌘/+/X', 'Ctrl/+/X'] },
  {
    section: CellSection,
    label: 'Paste cell(s)',
    items: ['⌘/+/V', 'Ctrl/+/V'],
  },
  {
    section: CellSection,
    label: 'Toggle cell(s) text color',
    items: ["⇧/+/⌘/+/'", "Shift/+/Ctrl/+/'"],
  },
  {
    section: CellSection,
    label: 'Toggle cell(s) background color',
    items: ['⇧/+/⌘/+/;', 'Shift/+/Ctrl/+/;'],
  },
  {
    section: CellSection,
    label: 'Grow cell(s) text',
    items: ['⇧/+/⌘/+/+', 'Shift/+/Ctrl/+/+'],
  },
  {
    section: CellSection,
    label: 'Shrink cell(s) text',
    items: ['⇧/+/⌘/+/-', 'Shift/+/Ctrl/+/-'],
  },
  {
    section: CellSection,
    label: 'Bold cell text',
    items: ['⌘/+/B', 'Ctrl/+/B'],
  },
  {
    section: CellSection,
    label: 'Italic cell text',
    items: ['⌘/+/I', 'Ctrl/+/I'],
  },
  {
    section: CellSection,
    label: 'Red BG, White Text',
    items: ['⌘/+/1', 'Ctrl/+/1'],
  },
  {
    section: CellSection,
    label: 'Normal BG, Red Text',
    items: ['⌘/+/2', 'Ctrl/+/2'],
  },
  {
    section: CellSection,
    label: 'Green BG, Black Text',
    items: ['⌘/+/3', 'Ctrl/+/3'],
  },
  {
    section: CellSection,
    label: 'Yellow BG, White Text',
    items: ['⌘/+/4', 'Ctrl/+/4'],
  },
  {
    section: CellSection,
    label: 'Clear all formatting',
    items: ['⌘/+/\\', 'Ctrl/+/\\'],
  },

  {
    section: TrackingSection,
    label: 'Start tracking on selected row',
    items: ['⌥/+/T', 'Alt/+/T'],
  },
  {
    section: TrackingSection,
    label: 'Write duration when tracking',
    items: ['⌥/+/Spacebar', 'Ctrl/+/Spacebar'],
  },
  {
    section: TrackingSection,
    label: 'Track next row',
    items: ['Spacebar', 'Spacebar'],
  },
  {
    section: TrackingSection,
    label: 'Track previous row',
    items: ['⇧/+/Spacebar', 'Shift/+/Spacebar'],
  },
  {
    section: MiscSection,
    label: 'Print via PDF',
    items: ['⌘/+/P', 'CTRL/+/P'],
  },
  {
    section: MiscSection,
    label: 'Open keyboard shortcuts modal',
    items: ['⌥/+/K', 'Alt/+/K'],
  },
  { section: MiscSection, label: 'Search', items: ['⌘/+/F', 'Ctrl/+/F'] },
  {
    section: MiscSection,
    label: 'Undo & Activity Feed',
    items: ['⌘/+/Z', 'Ctrl/+/Z'],
  },
  {
    section: MiscSection,
    label: 'Save a timestamped copy of your show',
    items: ['⌘/+/S', 'Ctrl/+/S'],
  },
  {
    section: MiscSection,
    label: 'Place a timestamped in the selected cell',
    items: ['⌘/+/G', 'Ctrl/+/G'],
  },
  {
    section: GlobalElementSection,
    label: 'Save as new global element',
    items: ['⇧/+/⌥/+/S', 'Shift/+/Alt/+/S'],
  },
  {
    section: GlobalElementSection,
    label: 'Publish current version to GE manager',
    items: ['⌥/+/S', 'Alt/+/S'],
  },
  {
    section: GlobalElementSection,
    label: 'Publish current version everywhere',
    items: ['⇧/+/⌘/+/S', 'Shift/+/Ctrl/+/S'],
  },
  {
    section: GlobalElementSection,
    label: 'Open GE insert modal',
    items: ['⌥/+/G', 'Alt/+/G'],
  },
  {
    section: GlobalElementSection,
    label: 'Retrieve latest GE changes',
    items: ['⌥/+/R', 'Alt/+/R'],
  },
];

const jumpShortcuts = [
  {
    section: RowSection,
    label: 'Jump to title row above',
    items: ['⌥/+/↑', 'Alt/+/↑'],
  },
  {
    section: RowSection,
    label: 'Jump to title row below',
    items: ['⌥/+/↓', 'Alt/+/↓'],
  },
  {
    section: RowSection,
    label: 'Jump to first row',
    items: ['⇧/+/⌥/+/↑', 'Shift/+/Alt/+/↑'],
  },
  {
    section: RowSection,
    label: 'Jump to last row',
    items: ['⇧/+/⌥/+/↓', 'Shift/+/Alt/+/↓'],
  },
];

/**
 * Keyboard Shortcuts Modal
 */
export default React.createClass({
  displayName: 'KeyboardShortcutsModal',

  contextTypes: {
    router: React.PropTypes.object.isRequired,
  },

  getInitialState() {
    return {
      keyboardShortcuts: KeyboardShortcuts.concat(jumpShortcuts),
      searchTerm: '',
      searchedKeyMatches: null,
      searchFocusState: false,
    };
  },

  render() {
    let { keyboardShortcuts, searchedKeyMatches } = this.state;

    let titleNode = this._getModalTitle();

    let hotKeys =
      searchedKeyMatches && searchedKeyMatches.length > 0
        ? searchedKeyMatches
        : keyboardShortcuts;

    let rowKeys = hotKeys.filter((key) => key.section === RowSection);
    let columnKeys = hotKeys.filter((key) => key.section === ColumnSection);
    let cellKeys = hotKeys.filter((key) => key.section === CellSection);
    let trackingKeys = hotKeys.filter((key) => key.section === TrackingSection);
    let miscKeys = hotKeys.filter((key) => key.section === MiscSection);
    let globalElementKeys = hotKeys.filter(
      (key) => key.section === GlobalElementSection,
    );

    let showRowKeys = rowKeys.length > 0;
    let showColumnKeys = columnKeys.length > 0;
    let showCellKeys = cellKeys.length > 0;
    let showTrackingKeys = trackingKeys.length > 0;
    let showMiscKeys = miscKeys.length > 0;
    let showGlobalElementKeys = globalElementKeys.length > 0;

    return (
      <Modal
        className="modal-generic-wrapper keyboard-shortcuts-modal"
        headerClassName="modal-generic-header"
        name={UIRegions.KeyboardShortcuts}
        title={<span>{titleNode}</span>}
      >
        <Row className="keyboard-shortcuts-modal">
          {showRowKeys ? (
            <Col lg={8} md={8} sm={8}>
              <h5>Row edit shortcuts</h5>
              {rowKeys.map((key) => this._getShortcutItem(key))}
            </Col>
          ) : null}

          {!showColumnKeys && !showCellKeys ? null : (
            <Col lg={8} md={8} sm={8}>
              {showColumnKeys ? (
                <div>
                  <h5>Column shortcuts</h5>
                  {columnKeys.map((key) => this._getShortcutItem(key))}
                </div>
              ) : null}
              {showCellKeys ? (
                <div>
                  <h5>Cell edit shortcuts</h5>
                  {cellKeys.map((key) => this._getShortcutItem(key))}
                </div>
              ) : null}
            </Col>
          )}

          {!showTrackingKeys && !showMiscKeys ? null : (
            <Col lg={8} md={8} sm={8}>
              {showTrackingKeys ? (
                <div>
                  <h5>Tracking shortcuts</h5>
                  {trackingKeys.map((key) => this._getShortcutItem(key))}
                </div>
              ) : null}
              {showMiscKeys ? (
                <div>
                  <h5>Misc shortcuts</h5>
                  {miscKeys.map((key) => this._getShortcutItem(key))}
                </div>
              ) : null}
            </Col>
          )}
          {showGlobalElementKeys ? (
            <Col lg={8} md={8} sm={8}>
              <h5>Global element shortcuts</h5>
              {globalElementKeys.map((key) => this._getShortcutItem(key))}
            </Col>
          ) : null}
        </Row>
      </Modal>
    );
  },

  _getModalTitle() {
    let { searchTerm } = this.state;

    return (
      <Row>
        <Col lg={17} md={17} sm={17}>
          <h4 className="keyboard-shortcuts-modal-title">Keyboard Shortcuts</h4>
        </Col>
        <Col lg={6} md={6} sm={6}>
          <div className="search-container-header-input">
            <span
              className={classnames('search-wrapper', {
                'has-focus': this.state.searchFocusState,
              })}
            >
              <Input
                type="text"
                autoFocus
                placeholder="Search for shortcut"
                className={classnames('search-input', {
                  'has-value': searchTerm.length > 0,
                })}
                value={searchTerm}
                addonBefore={<span>{this._getSearchPreControl()}</span>}
                onChange={(e) => this._handleSearchChange(e.target.value)}
                onFocus={() => this._toggleSearchInputFocus()}
                onBlur={() => this._toggleSearchInputFocus()}
              />
            </span>
          </div>
        </Col>
      </Row>
    );
  },

  _toggleSearchInputFocus() {
    let { searchFocusState } = this.state;
    this.setState({ searchFocusState: !searchFocusState });
  },

  _getSearchPreControl() {
    let { searchTerm } = this.state;
    let hasValue = searchTerm.length < 1 ? false : true;

    let searchNode = (
      <span className="search-prefix-wrapper">
        <Icon name="search" />
      </span>
    );
    let removeNode = (
      <span
        onClick={() => this._clearSearchTerm()}
        className="search-prefix-wrapper remove"
      >
        <Icon name="times" />
      </span>
    );

    return hasValue ? removeNode : searchNode;
  },

  _clearSearchTerm() {
    this.setState({ searchTerm: '', searchedKeyMatches: null });
  },

  _handleSearchChange(searchTerm) {
    this.setState({ searchTerm });

    let { keyboardShortcuts } = this.state;
    let searchRegex = new RegExp(searchTerm, 'ig');
    let newKeys = null;

    let keyMatches = keyboardShortcuts.filter((key) => {
      return key.label.match(searchRegex);
    });

    if (keyMatches.length > 0) newKeys = keyMatches;

    return this.setState({ searchedKeyMatches: newKeys });
  },

  /**
   * Get shortcut item with special style and label
   */
  _getShortcutItem(shortcut) {
    return (
      <Row className="keyboard-shortcut">
        {this._getShortcutValue(shortcut.items)}
        <div className="keyboard-shortcut-label">{shortcut.label}</div>
      </Row>
    );
  },

  /**
   * Get specially styled keyboard shortcut key value
   */
  _getShortcutValue(items) {
    let item = isMac() ? items[0] : items[1];
    let keyModifier = item.split('/+/');

    return keyModifier.map((key, indx) => {
      return (
        <div className="keyboard-shortcut-item-wrapper">
          <div className="keyboard-shortcut-item">{key}</div>
          <div className="keyboard-shortcut-item-add">
            {indx < keyModifier.length - 1 ? '+' : ''}
          </div>
        </div>
      );
    });
  },
});
